import React, { useState, useEffect, useMemo } from 'react';
import { Plus, MessageSquare, Trash2, Moon, Sun, Laptop, Edit2, Check, X, MoreVertical, Search, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { toast } from 'sonner';

export function Sidebar({ currentConversationId, onSelectConversation, onNewChat, theme, setTheme, refreshTrigger, user, onLogout, isOpen, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv => 
      conv.title?.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  useEffect(() => {
    loadConversations();
  }, [refreshTrigger]);

  const loadConversations = async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await api.delete(`/conversations/${conversationId}`);
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        onNewChat();
      }
      toast.success('Chat gelöscht');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleStartEdit = (e, conversation) => {
    e.stopPropagation();
    setEditingId(conversation.id);
    setEditTitle(conversation.title);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditTitle('');
  };

  const handleSaveEdit = async (e, conversationId) => {
    e.stopPropagation();
    if (!editTitle.trim()) {
      toast.error('Titel darf nicht leer sein');
      return;
    }
    
    try {
      await api.patch(`/conversations/${conversationId}?title=${encodeURIComponent(editTitle)}`);
      setConversations(conversations.map(c => 
        c.id === conversationId ? { ...c, title: editTitle } : c
      ));
      setEditingId(null);
      setEditTitle('');
      toast.success('Chat umbenannt');
    } catch (error) {
      console.error('Error updating conversation:', error);
      toast.error('Fehler beim Umbenennen');
    }
  };

  const handleKeyDown = (e, conversationId) => {
    if (e.key === 'Enter') {
      handleSaveEdit(e, conversationId);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };

  const cycleTheme = () => {
    const themes = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    
    if (nextTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setTheme(systemTheme);
      localStorage.setItem('theme', 'system');
    } else {
      setTheme(nextTheme);
    }
  };

  const getThemeIcon = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'system' || !savedTheme) {
      return <Laptop className="h-4 w-4" />;
    }
    return theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />;
  };

  return (
    <div className="w-80 border-r border-border bg-card flex flex-col h-screen overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold tracking-tight">Orq.ai</h2>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              title="Toggle theme"
              className="h-8 w-8"
            >
              {getThemeIcon()}
            </Button>
            {/* Close button for mobile */}
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 md:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <Button
          data-testid="new-chat-btn"
          onClick={onNewChat}
          className="w-full justify-start gap-2 bg-primary text-primary-foreground hover:bg-primary/90 mb-3"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </Button>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-chats-input"
            type="text"
            placeholder="Chats durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {searchQuery ? `Keine Chats für "${searchQuery}" gefunden` : 'Noch keine Chats'}
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                  currentConversationId === conv.id
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
              >
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                
                <div 
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => editingId !== conv.id && onSelectConversation(conv.id)}
                >
                  {editingId === conv.id ? (
                    <Input
                      data-testid="rename-chat-input"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, conv.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-sm w-full"
                      autoFocus
                    />
                  ) : (
                    <span className="text-sm truncate block">{conv.title}</span>
                  )}
                </div>
                {editingId === conv.id ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      data-testid="save-rename-btn"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleSaveEdit(e, conv.id)}
                      className="h-7 w-7"
                    >
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    </Button>
                    <Button
                      data-testid="cancel-rename-btn"
                      variant="ghost"
                      size="icon"
                      onClick={handleCancelEdit}
                      className="h-7 w-7"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="chat-menu-btn"
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 flex items-center justify-center h-7 w-7 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                        aria-label="Chat-Menü"
                      >
                        <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40 z-50">
                      <DropdownMenuItem
                        data-testid="rename-menu-item"
                        onClick={(e) => handleStartEdit(e, conv)}
                        className="cursor-pointer"
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Umbenennen
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="delete-menu-item"
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        className="cursor-pointer text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Löschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* User section with logout */}
      <div className="p-4 border-t border-border">
        {user && (
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="h-8 w-8 rounded-full shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={onLogout}
              title="Abmelden"
              className="h-8 w-8 shrink-0"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Powered by Orq.ai</p>
      </div>
    </div>
  );
}